import React, { useState, useEffect, useMemo, useCallback, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import moment from 'moment'
import PropTypes from 'prop-types'

import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import { ButtonGroup } from '@material-ui/core'
import {
  DoneAll as DoneAllIcon,
  Event as EventIcon
} from '@material-ui/icons'

import { useDialog } from '@galo-monitor/dialog'
import { Scope } from '@galo-monitor/forms'
import fertilizationSchema from '@galo-monitor/forms/schemas/property/activities/fertilization.schema'
import harvestSchema from '@galo-monitor/forms/schemas/property/activities/harvest.schema'
import pesticideApplicationSchema from '@galo-monitor/forms/schemas/property/activities/pesticideApplication.schema'
import seedingSchema from '@galo-monitor/forms/schemas/property/activities/seeding.schema'
import soilPreparationSchema from '@galo-monitor/forms/schemas/property/activities/soilPreparation.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { ActivityActions } from '@galo-monitor/stores/activity'
import ActivityConstants from '@galo-monitor/stores/activity/activityConstants'
import { selectLanguage } from '@galo-monitor/stores/language/selectorLanguage'
import { selectUnitOfMeasurement } from '@galo-monitor/stores/property/selectorProperty'
import Checkbox from '@galo-monitor/web-components/Checkbox'
import Form from '@galo-monitor/web-components/Form'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'

import FertilizationFields from './components/FertilizationFields'
import HarvestFields from './components/HarvestFields'
import PesticideApplicationFields from './components/PesticideApplicationFields'
import SeedingFields from './components/SeedingFields'
import SoilPreparationFields from './components/SoilPraparationFields'
import {
  Container,
  SectionTitle,
  TypeButton
} from './theme'

const ActivityForm = forwardRef((props, formRef) => {
  const {
    activity,
    activityState,
    currentHarvest,
    plots,
    loading,
    setLoading,
    onSuccess,
    onError
  } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const { createDialog } = useDialog()
  const snackbar = useSnackbar()

  const { selected } = useSelector(selectLanguage)
  const { initials } = useSelector(selectUnitOfMeasurement)

  const [stateSelected, setStateSelected] = useState(activityState)

  const {
    data: activityData,
    isValidating: isValidatingActivityData,
    cache
  } = useGaloMonitorApi(
    activity.id
      ? `/farms/properties/activities/show/${ activity.id }`
      : null
  )

  const schemaConstructor = useMemo(() => {
    switch(activity.type) {
      case ActivityConstants.FERTILIZATION_ACTIVITY_TYPE:
        return fertilizationSchema
      case ActivityConstants.HARVEST_ACTIVITY_TYPE:
        return harvestSchema
      case ActivityConstants.PESTICIDE_APPLICATION_ACTIVITY_TYPE:
        return pesticideApplicationSchema
      case ActivityConstants.SEEDING_ACTIVITY_TYPE:
        return seedingSchema
      case ActivityConstants.SOIL_PREPARATION_ACTIVITY_TYPE:
        return soilPreparationSchema
      default:
        return {}
    }
  }, [activity.type])

  const FormFields = useMemo(() => {
    switch(activity.type) {
      case ActivityConstants.FERTILIZATION_ACTIVITY_TYPE:
        return FertilizationFields
      case ActivityConstants.HARVEST_ACTIVITY_TYPE:
        return HarvestFields
      case ActivityConstants.PESTICIDE_APPLICATION_ACTIVITY_TYPE:
        return PesticideApplicationFields
      case ActivityConstants.SEEDING_ACTIVITY_TYPE:
        return SeedingFields
      case ActivityConstants.SOIL_PREPARATION_ACTIVITY_TYPE:
        return SoilPreparationFields
      default:
        return () => <></>
    }
  }, [activity.type])

  const dispatchSubmitAction = useCallback(data =>
    dispatch(ActivityActions.saveActivity(
      {
        ...data,
        id: activity.id,
        type: activity.type,
        harvest_id: activityData?.harvestId ?? currentHarvest.id,
        state: stateSelected
      },
      () => {
        snackbar.success(t('{this} {action} successfully!', {
          this: t('activity', { howMany: 1 }),
          action: toLower(t(isEmpty(activity) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
        }))
        onSuccess()
      },
      onError
    ))
  , [activity, activityData, currentHarvest.id, dispatch, onError, onSuccess, snackbar, stateSelected, t])

  const handleSubmit = useCallback(
    data => {
      setLoading(true)

      const selectedPlots = filter(
        isEmpty(data.plots)
          ? map(
            activityData.plots,
            plot => ({ id: plot.id })
          ) : map(
            data.plots,
            (plot, index) => plot.id ? ({ id: plots[index].id }) : null
          ),
        plot => plot
      )

      const formStartDate = moment(data.date_initial)
      const formEndDate = moment(data.date_initial)

      const harvestStartDate = moment(currentHarvest.dateInitial).format()
      const harvestEndDate = moment(currentHarvest.dateFinish).format()

      const isOutOfDateBoundaries = () =>
        formStartDate.isBefore(harvestStartDate) ||
        formStartDate.isAfter(harvestEndDate) ||
        formEndDate.isBefore(harvestStartDate) ||
        formEndDate.isAfter(harvestEndDate)

      if (currentHarvest.dateInitial && currentHarvest.dateFinish && isOutOfDateBoundaries()) {
        createDialog({
          id: 'error-activity',
          Component: ConfirmationModal,
          props: {
            title: t('action needed'),
            message: t('you are registering an action out of harvest planned period'),
            buttonTitle: t('register'),
            buttonProps: {
              color: 'primary'
            },
            onConfirm: () => dispatchSubmitAction({ ...data, plots: selectedPlots })
          }
        })
        return
      }

      dispatchSubmitAction({ ...data, plots: selectedPlots })
    },
    [activityData, createDialog, currentHarvest.dateFinish, currentHarvest.dateInitial, dispatchSubmitAction, plots, setLoading, t]
  )

  useEffect(() => {
    if (!isEmpty(activityData)) {
      setLoading(false)

      if (!isEmpty(plots)) {
        if (activityData.status === 'planned')
          setStateSelected('plans')

        if (activityData.status === 'realized')
          setStateSelected('completed')
      }
    }
  }, [setLoading, isValidatingActivityData, activityData, plots])

  useEffect(() => {
    activity.id && setLoading(true)
    return () => cache.delete(`/farms/properties/activities/show/${ activity.id }`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ schemaConstructor }
      onSubmit={ handleSubmit }
    >
      <Container>
        {!isEmpty(plots) && (
          <Container column>
            <SectionTitle variant="h6">
              {t('inform the {this}', {
                this: t('plot', { howMany: 2 }),
                howMany: 2
              })}
            </SectionTitle>
            {map(plots, ({ plot, convertedArea }, index) => (
              <Scope path={ `plots[${ index }]` } key={ index }>
                <Checkbox
                  name="id"
                  label={ `${ plot.name } / ${ convertedArea } ${ initials }` }
                  defaultValue={
                    !!find(
                      activity.plots,
                      activityPlot => activityPlot.id === plot.id
                    )
                  }
                  onFieldValueChange={ isSelectedPlot => {
                    if (formRef.current?.getFieldRef('area_total')) {
                      const { area_total: areaTotal } = formRef.current.getData()

                      const calcValue = isSelectedPlot
                        ? Number(areaTotal) + Number(convertedArea)
                        : Number(areaTotal) - Number(convertedArea)
                      formRef.current.setFieldValue('area_total', calcValue.toString())
                    }
                  } }
                />
              </Scope>
            ))}
          </Container>
        )}
        <FormFields
          t={ t }
          activity={ activityData }
          formRef={ formRef }
          loading={ loading && isValidatingActivityData }
          locale={ selected }
          stateTypeButtons={
            <ButtonGroup variant="contained" style={ { margin: '20px 0' } }>
              {!isEmpty(plots) && (
                <TypeButton
                  color={ stateSelected === 'plans' ? 'primary' : '' }
                  onClick={ () => setStateSelected('plans') }
                >
                  <EventIcon />
                  {t('planned', { howMany: 1 })}
                </TypeButton>
              )}
              <TypeButton
                color={ stateSelected !== 'plans' ? 'primary' : '' }
                onClick={ () => setStateSelected('completed') }
              >
                <DoneAllIcon />
                {t('realized', { howMany: 1 })}
              </TypeButton>
            </ButtonGroup>
          }
          unitInitials={ initials }
        />
      </Container>
    </Form>
  )
})

ActivityForm.propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string.isRequired,
    plots: PropTypes.array
  }),
  activityState: PropTypes.string.isRequired,
  currentHarvest: PropTypes.object.isRequired,
  plots: PropTypes.array,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

ActivityForm.defaultProps = {
  activity: {
    id: null,
    plots: []
  },
  plots: [],
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default ActivityForm
